import * as fnvPlus from 'fnv-plus';
import JSZip from "jszip";
import { IAccessCredentialCreateResponse } from "../api/entities/access-credential";
import { IBucketV2ExportResponse, ICognitoTokenV2ExportResponse, IDeviceConfigV2ExportResponse, IEnvironmentSettingsResponse, IExportFileNameData, IPolicyV2ExportResponse, IStorageApplicationRoleConfigResponse, IShardRepositoryDeviceTokenV2ExportResponse, IShardRepositoryInfoV2ExportResponse, IShardRepositoryExportResponse, IStoragePoolV2ExportResponse } from "../api/entities/bzs-config";
import { IIdName } from "../api/entities/id-name";
import { IPageRequestParams, ISortRequestParams } from "../api/entities/page";
import { IBucketDetailsItem } from "../api/entities/bucket";
import { IShardRepositoryRaw, IShardRepository } from "../api/entities/shard-repository";
import { IShardClusterDetailsItem } from "../api/entities/shard-cluster";
import { IShardClusterRepositoryCredentialStatus } from "../components/shard-clusters/panels/shard-cluster-credentials-update-panel/shard-cluster-credentials-update-panel";
import { storageExternalFactory } from "./factories/storage-external-factory";
import { v4 as uuidv4 } from 'uuid';

class BzsConfigExportUtils {
    getExportParams<TSearchParams extends IPageRequestParams & ISortRequestParams<any>>(searchParams: TSearchParams): TSearchParams {
        return {
            ...searchParams,
            offset: null,
            pageSize: null,
        };
    }

    async getBZsConfigData(application: IIdName<string>,
                           credential: IAccessCredentialCreateResponse,
                           bucket: IBucketDetailsItem,
                           storagePool: IShardClusterDetailsItem,
                           shardClusterRepositories: IShardClusterRepositoryCredentialStatus[]
    ): Promise<IExportFileNameData[]>{
        return await this.getExportFilesData(application, credential, bucket, storagePool, shardClusterRepositories);
    }

    async createArchive(files: IExportFileNameData[]): Promise<string> {
        const zip = new JSZip();

        files.forEach(x => {
            zip.file(x.name, JSON.stringify(x.data));
        })

        return await zip.generateAsync({ type: 'base64' });
    }

    private async getExportFilesData(application: IIdName<string>, credential: IAccessCredentialCreateResponse, bucket: IBucketDetailsItem, storagePool: IShardClusterDetailsItem, shardClusterRepositories: IShardClusterRepositoryCredentialStatus[]): Promise<IExportFileNameData[]> {
        let repoURI = this.maskValue(bucket.tagURI);
        let policyURI = this.maskValue(`tag:myota.io,2019:policy/${bucket.bucketConfig.bucketId}`);

        let did = this.generateDid();
        let deviceKey = this.generateKey();

        let storageNodes = shardClusterRepositories.map(x => x.shardRepositoryDetails);

        let filesData: IExportFileNameData[] = [];
        filesData.push(this.getBucketDetails(bucket, repoURI, storagePool, storageNodes));
        filesData.push(this.getCognitoTokenStub());
        filesData.push(this.getDeviceConfig(application, did, deviceKey, repoURI, policyURI));
        filesData.push(this.getCredentialConfig(credential, application));
        filesData.push(this.getEnvironmentSettings(did, deviceKey, application));
        filesData.push(this.getPolicyConfig(policyURI, bucket));

        await Promise.all(storageNodes.map(x => this.getStorageNodeDetails(x, did)))
            .then(values => filesData.push(...values));

        return filesData;
    }

    private getBucketDetails(bucket: IBucketDetailsItem,
                             repoURI: string,
                             storagePool: IShardClusterDetailsItem,
                             storageNodes: IShardRepositoryRaw[],
    ): IExportFileNameData<IBucketV2ExportResponse> {
        let storageNodesDict = storageNodes.reduce((acc, x) => {
            acc[x.id] = x;
            return acc;
        }, {});

        const getStorage = (x: IShardRepository): IShardRepositoryInfoV2ExportResponse => ({
            nodeId: x.id,
            type: storageNodesDict[x.id].storageType,
            bucket: storageNodesDict[x.id].bucket ?? storageNodesDict[x.id].container,
            region: storageNodesDict[x.id].region
        });

        const getStorageNodeExportResponse = (type: string): IShardRepositoryExportResponse =>
            ({
                redundancyLevel: storagePool[`${type}RedundancyLevel`],
                securityLevel: storagePool[`${type}SecurityLevel`],
                storage: storagePool[`${type}StorageNodes`].map(x => getStorage(x))
            });
        let storagePoolInfo: IStoragePoolV2ExportResponse = {
            id: storagePool.id,
            contentNodes: getStorageNodeExportResponse('content'),
            metaNodes: getStorageNodeExportResponse('meta'),
            keyNodes: getStorageNodeExportResponse('key'),
        };

        let name = `api/v1/repo/${repoURI}`;
        let data: IBucketV2ExportResponse = {
            repository: {
                uri: repoURI,
                rootPath: bucket.id,
                folderName: bucket.name,
                storagePool: storagePoolInfo as IStoragePoolV2ExportResponse
            }
        };
        return { name, data };
    }

    async getStorageNodeDetails(storageNode: IShardRepositoryRaw, did?: string): Promise<IExportFileNameData<IShardRepositoryDeviceTokenV2ExportResponse>> {
        let storage = storageExternalFactory.getStorage(storageNode.storageType);
        let token = storage.getToken(storageNode);

        let name = did ? `api/v1/device/${did}/token/${storageNode.id}` : storageNode.id;
        let data: IShardRepositoryDeviceTokenV2ExportResponse = {
            storageId: storageNode.id,
            type: storageNode.storageType,
            token
        };
        return { name, data };
    }

    private getCognitoTokenStub(): IExportFileNameData<ICognitoTokenV2ExportResponse> {
        let name = 'api/v1/account/cognito/token';
        let data: ICognitoTokenV2ExportResponse = {
            token_type: "Bearer",
            expires_in: 900,
            access_token: "access",
            refresh_token: "refresh"
        };

        return { name, data };
    }

    private getCredentialConfig(credential: IAccessCredentialCreateResponse, application: IIdName<string>): IExportFileNameData<IStorageApplicationRoleConfigResponse> {
        let name = `api/v1/storapp/${application.name}/role/config`;
        let data: IStorageApplicationRoleConfigResponse = {
            roleCredentials: [
                {
                    role: credential.name,
                    accessKey: credential.accessKey,
                    secret: credential.accessSecret
                }
            ]
        };

        return { name, data };
    }

    private getDeviceConfig(application: IIdName<string>, did: string, deviceKey: string, repoURI: string, policyURI: string): IExportFileNameData<IDeviceConfigV2ExportResponse> {
        let name = `api/v1/device/${did}/config`;
        let data: IDeviceConfigV2ExportResponse = {
            device: {
                name: application.name,
                did,
                key: deviceKey,
                os: 'storage-application',
                status: 'active',
                approval: true,
                blocked: false
            },
            repos: [
                { repoURI, policyURI }
            ]
        };

        return { name, data };
    }

    private getEnvironmentSettings(did: string, deviceKey: string, application: IIdName<string>): IExportFileNameData<IEnvironmentSettingsResponse> {
        let name = `env.json`;
        let data: IEnvironmentSettingsResponse = {
            deviceId: did,
            deviceKey,
            appName: application.name
        };

        return { name, data };
    }

    private getPolicyConfig(policyURI: string, bucket: IBucketDetailsItem): IExportFileNameData<IPolicyV2ExportResponse> {
        let name = `api/v2/policy/${policyURI}`;
        let data: IPolicyV2ExportResponse = {
            uri: policyURI,
            name: `${bucket.name}-policy`,
            isDefault: true,
            maxDeviceMB: -1,
            maxDevicesPerUser: -1,
            notifyMB: -1,
            shouldNotify: false,
            previousVersionRetentionDays: 0,
            previousVersionRetentionMB: -1,
            deletedFilesRetentionDays: bucket.bucketConfig.retentionDays ?? 0,
            deletedFilesRetentionMB: -1,
            enableOffline: false,
            localAuth: {
                enable: false,
                timerInMinutes: -1
            }
        };

        return { name, data };
    }

    private maskValue(value: string) {
        return fnvPlus.hash(value, 64).str();
    }

    private generateDid(): string {
        let letters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
        const at = `0123456789-_${letters}${letters.toLowerCase()}`;
        return this.generateString(at, 20);
    }

    private generateKey(): string {
        return uuidv4();
    }

    private generateString(at: string, length: number) {
        const kb = new Uint8Array(length);
        crypto.getRandomValues(kb);

        let r: string[] = new Array<string>(length);
        for (let i = 0; i < length; i++) {
            r[i] = at[kb[i] % at.length];
        }
        return r.join('');
    }
}

export const bZsConfigExportUtils = new BzsConfigExportUtils();
